import React, {Component} from 'react';
import {bindActionCreators, Dispatch} from 'redux';
import {connect} from 'react-redux';

import {TabPanel, TabView, TabViewTabChangeParams} from 'primereact/tabview';

import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Reference from '~/shared/src/ci/Reference';
import ReferencePropertySet from '~/shared/src/mm/ReferencePropertySet';

import ReferencePropertiesToolbar from '~/frontend/src/components/base/ItemFinder/detail/ReferencePanel/ReferenceDetailPanel/ReferencePropertyTabPanel/ReferencePropertiesToolbar';
import ReferencePropertiesDataTable from '~/frontend/src/components/base/ItemFinder/detail/ReferencePanel/ReferenceDetailPanel/ReferencePropertyTabPanel/ReferencePropertiesDataTable';
import EnumReferencePropertiesDataTable from '~/frontend/src/components/base/ItemFinder/detail/ReferencePanel/ReferenceDetailPanel/EnumReferencePropertyDataTable';

import {StoreState} from '~/frontend/src/store';

import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import * as DataActions from '~/frontend/src/actions/data';
import {UpdateReference} from '~/frontend/src/actions/data/updateReference';


const Actions = Object.assign( {}, PanelActions, DataActions ) as any;
type SetParam = <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof AllProps ) => UnsetParamAction;


const defaultProps = {
    ...defaultBaseRequestParameters,

    /**
     * Show empty (not set) properties.
     */
    showEmptyProperties: true as boolean,

    /**
     * Show default values for properties.
     */
    showPropertyDefaults: true as boolean,

    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    /**
     * Is data in this form editable at all?
     */
    editable: false as boolean,

    /**
     * The index of the active tab.
     */
    activeTabIndex: 0 as number
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The reference.
     */
    reference : Reference;

    /**
     * Parameters for deep linking of a property
     */
    deepLinkProps? : {
        property : string;
    };

    /**
     * The props for sub panels
     */
    subPanelProps? : any;
};

type ActionProps = {
    // common panel actions
    setParam : SetParam;
    unsetParam : UnsetParam;

    // data actions
    updateReference : UpdateReference;
}

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & ActionProps;

/**
 * ReferencePropertyTabPanel
 */
class ReferencePropertyTabPanel extends Component<AllProps> {

    static defaultProps = defaultProps;

    /**
     * REDUX stuff
     */
    constructor( props : AllProps ) {
        super( props );

        // bind event handlers
        this.onTabViewTabChange = this.onTabViewTabChange.bind( this );
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.processDeepLink();
    }


    /* private ****************************************************************************************************************************/

    /**
     * Processes given deepLinkProps.
     *
     * @private
     */
    processDeepLink() {
        if ( this.props.deepLinkProps ) {
            const {
                      uid,
                      reference,
                      deepLinkProps
                  }                      = this.props,
                  referencePropertySetId = metaModel[ schema ].getReferencePropertySetForReferenceSetAndPropertyType( reference.referenceSetId, deepLinkProps.property )?.id;

            const clearDeepLinkProps = true;
            let activeTabIndex : number | undefined;

            if ( referencePropertySetId ) {
                const referencePropertySets = metaModel[ schema ].getReferencePropertySetsForReferenceSet( reference.referenceSetId );

                let enumReferenceSetCount = 1;

                for ( const referencePropertySet of referencePropertySets ) {
                    if ( referencePropertySet.isEnumerated ) {
                        if ( referencePropertySet.id === referencePropertySetId ) {
                            activeTabIndex = enumReferenceSetCount;
                            break;
                        }

                        enumReferenceSetCount++;
                    }
                }
            }

            if ( activeTabIndex ) {
                this.props.setParam( uid, 'activeTabIndex', activeTabIndex );
            }

            if ( clearDeepLinkProps ) {
                this.props.unsetParam( uid, 'deepLinkProps' );
            }
        }
    }

    isReferenceEditable() : boolean {
        const {
                  schema,
                  timestamp,
                  reference
              }                 = this.props,
              editableStatusIds = metaModel[ schema ].getEditableStatus().map( status => status.id );

        let retVal = false;

        if ( reference && timestamp === 'now' && editableStatusIds.includes( reference.statusId ) ) {
            retVal = true;
        }

        return retVal;
    }

    /**
     * Handler for TabView tabChange.
     *
     * @private
     */
    onTabViewTabChange( event : TabViewTabChangeParams ) {
        this.props.setParam( this.props.uid, 'activeTabIndex', event.index );
    }

    getPropertyTabPanels() : JSX.Element[] {
        const retVal : JSX.Element[] = [],
              {
                  uid,
                  dataContext,
                  schema,
                  status,
                  timezone,
                  timestamp,
                  reference,
                  rawMode,
                  showAdditionalRawNames,
                  showEmptyProperties,
                  showPropertyDefaults,
                  editable,
                  deepLinkProps,
                  subPanelProps
              }                      = this.props,
              referencePropertySets  = reference
                  ? metaModel[ schema ].getReferencePropertySetsForReferenceSet( reference.referenceSetId )
                  : [] as ReferencePropertySet[],
              hasProperties          = referencePropertySets.some( referencePropertySet => !referencePropertySet.isEnumerated ),
              dataTableKey           = 'referencePropertiesDataTable';

        let dataTableProps = subPanelProps?.[ dataTableKey ] ?? {};

        // properties
        if ( hasProperties ) {

            // apply deepLinkProps if
            if ( deepLinkProps ) {
                dataTableProps = {
                    ...dataTableProps,
                    deepLinkProps
                };
            }

            retVal.push( <TabPanel
                key="properties"
                header={__( 'YN.base.finder.properties.label' )}
                contentClassName="yn-flex-layout-parent"
            >
                <ReferencePropertiesToolbar
                    uid={uid}
                    showEmptyProperties={showEmptyProperties}
                    showPropertyDefaults={showPropertyDefaults}
                />
                <ReferencePropertiesDataTable
                    uid={`${uid}_${dataTableKey}`}
                    dataContext={dataContext}

                    schema={schema}
                    status={status}
                    timestamp={timestamp}
                    timezone={timezone}

                    referenceId={reference.id}
                    referenceSetId={reference.referenceSetId}
                    referenceStatusId={reference.statusId}
                    properties={reference.properties}

                    showEmptyProperties={showEmptyProperties}
                    showPropertyDefaults={showPropertyDefaults}

                    rawMode={rawMode}
                    showAdditionalRawNames={showAdditionalRawNames}
                    editable={editable && this.isReferenceEditable()}

                    {...dataTableProps}
                />
            </TabPanel> );
        }

        // enum properties
        for ( const referencePropertySet of referencePropertySets ) {
            if ( referencePropertySet.isEnumerated ) {
                retVal.push( <TabPanel
                    key={`enumProperty_${referencePropertySet.name}`}
                    header={referencePropertySet.label}
                    contentClassName="yn-flex-layout-parent"
                >
                    <EnumReferencePropertiesDataTable
                        uid={`${uid}_${dataTableKey}`}
                        dataContext={dataContext}

                        schema={schema}
                        status={status}
                        timestamp={timestamp}
                        timezone={timezone}

                        referenceId={reference.id}
                        referenceStatusId={reference.statusId}
                        referencePropertySet={referencePropertySet}
                        enumProperty={reference.enumProperties.get( referencePropertySet.propertyTypeId )}

                        rawMode={rawMode}
                        showAdditionalRawNames={showAdditionalRawNames}
                        editable={editable && this.isReferenceEditable()}

                        {...dataTableProps}
                    />
                </TabPanel> );
            }
        }

        return retVal;
    }

    /**
     * REDUX stuff
     */
    render() {
        const tabPanels = this.getPropertyTabPanels();

        if ( tabPanels.length ) {
            return <TabView
                activeIndex={this.props.activeTabIndex}
                onTabChange={this.onTabViewTabChange}
                className="yn-flex-layout-parent"
            >
                {tabPanels}
            </TabView>;
        }
        else {
            return '';
        }
    }
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<undefined, ActionProps, ComponentProps, StoreState>( undefined, mapDispatchToProps )( ReferencePropertyTabPanel );
